<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/agent/dashboard">Agent</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Team</span>
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Team</h1>
        <p class="page-head__subtitle">
          Everyone on your agency's team shares the same client portfolio. Invite colleagues and
          manage their access here.
        </p>
      </div>
      @if (pageState() === 'ready') {
        <div class="page-head__actions">
          <button type="button" class="agent-cta agent-cta--solid" (click)="toggleInvite()">
            <app-icon [name]="showInvite() ? 'x' : 'plus'" [size]="18" />
            <span>{{ showInvite() ? 'Cancel' : 'Invite an agent' }}</span>
          </button>
        </div>
      }
    </div>
  </header>

  @switch (pageState()) {
    @case ('loading') {
      <div class="dash-skeleton" style="min-height: 240px"></div>
    }
    @case ('no-agency') {
      <div class="dash-panel">
        <div class="dash-empty">
          <span class="dash-empty__icon"><app-icon name="users" [size]="40" /></span>
          <p class="dash-empty__title">You're not part of an agency</p>
          <p class="dash-empty__body">
            Team management is for agency members. You're set up as an individual agent.
          </p>
          <a class="agent-link" routerLink="/agent/dashboard">Back to dashboard</a>
        </div>
      </div>
    }
    @case ('forbidden') {
      <div class="dash-panel">
        <div class="dash-empty">
          <span class="dash-empty__icon"><app-icon name="shield" [size]="40" /></span>
          <p class="dash-empty__title">Admins manage the team</p>
          <p class="dash-empty__body">
            Only an agency admin can invite or manage members. Ask an admin on your team.
          </p>
          <a class="agent-link" routerLink="/agent/dashboard">Back to dashboard</a>
        </div>
      </div>
    }
    @case ('error') {
      <div class="dash-panel">
        <div class="dash-empty">
          <span class="dash-empty__icon"><app-icon name="alert-triangle" [size]="32" /></span>
          <p class="dash-empty__title">Couldn't load your team</p>
          <p class="dash-empty__body">Something went wrong on our side.</p>
        </div>
      </div>
    }
    @case ('ready') {
      @if (agency(); as ag) {
        <section class="dash-panel" aria-label="Agency team">
          @if (showInvite()) {
            <form class="agent-invite" [formGroup]="inviteForm" (ngSubmit)="submitInvite()">
              <div class="agent-invite__row">
                <label class="agent-invite__field">
                  <span>First name</span>
                  <input type="text" formControlName="first_name" autocomplete="given-name" />
                </label>
                <label class="agent-invite__field">
                  <span>Last name</span>
                  <input type="text" formControlName="last_name" autocomplete="family-name" />
                </label>
              </div>
              <div class="agent-invite__row">
                <label class="agent-invite__field">
                  <span>Email</span>
                  <input type="email" formControlName="email" autocomplete="email" />
                </label>
                <label class="agent-invite__field">
                  <span>Role</span>
                  <select formControlName="role">
                    <option value="AGENT">Agent</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </label>
              </div>
              @if (inviteError(); as err) {
                <p class="agent-invite__error">{{ err }}</p>
              }
              <div class="agent-invite__actions">
                <button
                  type="submit"
                  class="agent-cta agent-cta--solid"
                  [disabled]="inviteSubmitting()"
                >
                  {{ inviteSubmitting() ? 'Sending…' : 'Send invitation' }}
                </button>
              </div>
            </form>
          }

          <ul class="agent-team-list">
            @for (member of members(); track member.short_id) {
              <li class="agent-team">
                <span class="agent-team__avatar"><app-icon name="users" [size]="18" /></span>
                <span class="agent-team__main">
                  <span class="agent-team__name">
                    {{ member.name || member.email }}
                    @if (isSelf(member)) {
                      <span class="agent-team__you">You</span>
                    }
                  </span>
                  <span class="agent-team__email">{{ member.email }}</span>
                </span>
                <span
                  class="agent-team__role"
                  [class.agent-team__role--admin]="member.role === 'ADMIN'"
                  >{{ member.role === 'ADMIN' ? 'Admin' : 'Agent' }}</span
                >
                <span class="agent-team__actions">
                  @if (member.role === 'AGENT') {
                    <button type="button" (click)="changeRole(member, 'ADMIN')">Make admin</button>
                  } @else if (!isOnlyAdmin(member)) {
                    <button type="button" (click)="changeRole(member, 'AGENT')">Make agent</button>
                  }
                  @if (!isOnlyAdmin(member)) {
                    <button
                      type="button"
                      class="agent-team__remove"
                      (click)="removeMember(member)"
                    >
                      Remove
                    </button>
                  }
                </span>
              </li>
            }

            @for (invite of pendingInvites(); track invite.short_id) {
              <li class="agent-team agent-team--pending">
                <span class="agent-team__avatar agent-team__avatar--pending">
                  <app-icon name="clock" [size]="18" />
                </span>
                <span class="agent-team__main">
                  <span class="agent-team__name">{{ invite.email }}</span>
                  <span class="agent-team__email agent-team__email--pending">
                    Invited — awaiting acceptance
                  </span>
                </span>
                <span class="agent-team__role agent-team__role--pending">Pending</span>
                <span class="agent-team__actions">
                  <button type="button" class="agent-team__remove" (click)="cancelInvite(invite)">
                    Cancel
                  </button>
                </span>
              </li>
            }
          </ul>
        </section>
      }
    }
  }
</div>
